console.log("🎯 useWebRTC hook loaded - version with STUN servers");
// frontend/src/hooks/useWebRTC.js
import { useState, useRef, useCallback, useEffect } from 'react';
import socketService from '../services/socketService';
import callAPI from '../services/callService';

const useWebRTC = (localVideoRef, remoteVideoRef) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [callType, setCallType] = useState('audio');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [error, setError] = useState(null);
  const [callStatus, setCallStatus] = useState('idle');

  const peerConnection = useRef(null);
  const currentCallRef = useRef(null);
  const localStreamRef = useRef(null);
  const isAnswering = useRef(false);

  // STUN servers configuration
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  // Get the other user's ID
  const getOtherUserId = useCallback(() => {
    console.log('🔍 DEBUG getOtherUserId called');
    console.log('🔍 currentCallRef.current:', currentCallRef.current);
    console.log('🔍 currentCallRef.current?.caller:', currentCallRef.current?.caller);
    console.log('🔍 currentCallRef.current?.caller?._id:', currentCallRef.current?.caller?._id);
    console.log('🔍 currentCallRef.current?.receiver?._id:', currentCallRef.current?.receiver?._id);
    
    if (!currentCallRef.current) {
      console.log('❌ getOtherUserId: currentCallRef.current is null');
      return null;
    }
    
    const user = JSON.parse(localStorage.getItem('user'));
    console.log('🔍 User from localStorage:', user);
    
    if (!user || !user.id) {
      console.log('❌ getOtherUserId: user or user.id is null');
      return null;
    }
    
    const callerId = currentCallRef.current.caller?._id || currentCallRef.current.caller;
    const receiverId = currentCallRef.current.receiver?._id || currentCallRef.current.receiver;
    
    console.log('🔍 Caller ID:', callerId);
    console.log('🔍 Receiver ID:', receiverId);
    console.log('🔍 Current user ID:', user.id);
    
    const otherUserId = callerId === user.id ? receiverId : callerId;
    console.log('🔍 Calculated other user ID:', otherUserId);
    
    return otherUserId;
  }, []);;

  // Initialize local media stream
  const initializeLocalStream = useCallback(async (type = 'audio') => {
    try {
      console.log('🎥 Initializing local stream for:', type);
      
      // Stop existing stream if any
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: type === 'video' ? {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        } : false
      };

      console.log('📹 Requesting media with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Local stream obtained:', stream);
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        console.log('✅ Local video ref updated');
      }

      return stream;
    } catch (error) {
      console.error('❌ Error accessing media devices:', error);
      throw new Error(`Failed to access ${type} devices: ${error.message}`);
    }
  }, [localVideoRef]);

  // End call function
  const endCall = useCallback(async () => {
    try {
      console.log('📞 Ending call...');
      
      // Send end call notification if we have an active call
      if (currentCallRef.current) {
        console.log('📡 Sending end call API request for:', currentCallRef.current._id);
        try {
          await callAPI.endCall(currentCallRef.current._id);
        } catch (apiError) {
          console.error('❌ API error ending call:', apiError);
        }

        // Emit socket event to notify other user
        const targetUserId = getOtherUserId();
        if (targetUserId) {
          socketService.emitEndCall({
            callId: currentCallRef.current._id,
            targetUserId
          });
        }
      }

      // Clean up peer connection
      if (peerConnection.current) {
        peerConnection.current.onicecandidate = null;
        peerConnection.current.ontrack = null;
        peerConnection.current.onconnectionstatechange = null;
        peerConnection.current.close();
        peerConnection.current = null;
        console.log('✅ Peer connection closed');
      }

      // Clean up local stream
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          console.log('🛑 Stopping track:', track.kind);
          track.stop();
        });
        localStreamRef.current = null;
        setLocalStream(null);
        console.log('✅ Local stream stopped');
      }

      // Clean up remote stream
      setRemoteStream(null);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }

      // Reset states
      setIsCallActive(false);
      setIsCallInProgress(false);
      setCurrentCall(null);
      currentCallRef.current = null;

      console.log('✅ Call ended completely');
    } catch (error) {
      console.error('❌ Error ending call:', error);
    }
  }, [getOtherUserId, remoteVideoRef]);

  // Initialize peer connection
  const createPeerConnection = useCallback(() => {
    try {
      console.log('🔗 Creating peer connection...');
      const pc = new RTCPeerConnection(iceServers);
      
      // Add local stream to connection
      if (localStreamRef.current) {
        console.log('➕ Adding local tracks to peer connection');
        localStreamRef.current.getTracks().forEach(track => {
          pc.addTrack(track, localStreamRef.current);
          console.log('✅ Added track:', track.kind);
        });
      }

      // Handle remote stream
      pc.ontrack = (event) => {
        console.log('📹 Remote track received:', event.streams[0]);
        const remoteStream = event.streams[0];
        setRemoteStream(remoteStream);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
          console.log('✅ Remote video ref updated');
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        console.log('❄️ ICE candidate event:', event.candidate ? 'candidate generated' : 'all candidates generated');
        if (event.candidate && currentCallRef.current) {
          const targetUserId = getOtherUserId();
          if (targetUserId) {
            console.log('📤 Sending ICE candidate to:', targetUserId);
            
            socketService.emitICECandidate({
              callId: currentCallRef.current._id,
              candidate: event.candidate,
              targetUserId
            });
          }
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log('🔗🔗🔗 CONNECTION STATE CHANGED:', pc.connectionState);
        console.log('🔗 Previous isCallActive:', isCallActive);
        console.log('🔗 Previous isCallInProgress:', isCallInProgress);
        switch (pc.connectionState) {
          case 'connected':
            setIsCallActive(true);
            console.log('✅ WebRTC connection established');
            break;
          case 'disconnected':
          case 'failed':
            console.log('❌ WebRTC connection failed, ending call');
            endCall();
            break;
          case 'closed':
            console.log('🔒 WebRTC connection closed');
            break;
        }
      };

      // Handle ICE connection state
      pc.oniceconnectionstatechange = () => {
        console.log('🧊🧊🧊 ICE CONNECTION STATE:', pc.iceConnectionState);
        console.log('🧊 Signaling state:', pc.signalingState);
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          console.log('❌ ICE connection failed');
        }
      };

      peerConnection.current = pc;
      console.log('✅ Peer connection created successfully');
      return pc;
    } catch (error) {
      console.error('❌ Error creating peer connection:', error);
      throw error;
    }
  }, [endCall, getOtherUserId]);

  // Start a call
  const startCall = useCallback(async (conversationId, type = 'audio') => {
    try {
      console.log('🚀 START CALL PROCESS STARTED');
      console.log('📞 Starting call...', { conversationId, type });
      
      setIsCallInProgress(true);
      setCallType(type);

      // Step 1: Initialize local stream
      console.log('🎥 Step 1: Initializing local stream...');
      await initializeLocalStream(type);
      console.log('✅ Local stream initialized');
      
      // Step 2: Create peer connection
      console.log('🔗 Step 2: Creating peer connection...');
      const pc = createPeerConnection();
      console.log('✅ Peer connection created');

      // Step 3: Create offer
      console.log('📝 Step 3: Creating offer...');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: type === 'video'
      });
      console.log('✅ Offer created:', offer.type);
      
      await pc.setLocalDescription(offer);
      console.log('✅ Local description set');

      // Step 4: Send offer to server
      console.log('📡 Step 4: Sending offer to server...');
      const response = await callAPI.createCallOffer({
        conversationId,
        callType: type,
        offer
      });

      console.log('✅ Server response:', response);
      
      if (response.data && response.data.call) {
        const call = response.data.call;
        setCurrentCall(call);
        currentCallRef.current = call;
        console.log('✅ Call created on server:', call._id);
        
        // Return the call object - DON'T end the call here
        console.log('⏳ Waiting for receiver to answer the call...');
        return call;
      } else {
        throw new Error('No call data returned from server');
      }

    } catch (error) {
      console.error('❌ ERROR in startCall:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Clean up on error
      await endCall();
      throw error;
    }
  }, [initializeLocalStream, createPeerConnection, endCall]);

  // Answer incoming call
  const answerCall = useCallback(async (call, type = 'audio') => {
    // Prevent duplicate answers
    if (isAnswering.current) {
      console.log('⚠️ Already answering this call, skipping duplicate');
      return;
    }
    isAnswering.current = true;
    
    try {
      console.log('📞 Answering call...', call._id);
      
      setIsCallInProgress(true);
      setCallType(type);
      setCurrentCall(call);
      currentCallRef.current = call;

      // Initialize local stream
      await initializeLocalStream(type);
      
      // Create peer connection
      const pc = createPeerConnection();
      
      // Set remote description from offer
      await pc.setRemoteDescription(new RTCSessionDescription(call.offer));
      console.log('✅ Remote description set from offer');

      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('✅ Answer created and local description set');

      // Send answer to server
      await callAPI.answerCall(call._id, {
        answer
      });

      // Also send answer via socket for real-time update
      console.log('🔍 DEBUG: call object structure:', JSON.stringify(call));
      console.log('🔍 DEBUG: call.caller:', call.caller);
      console.log('🔍 DEBUG: call.caller?._id:', call.caller?._id);
      const targetUserId = call.caller?._id || call.caller;
      console.log('🔍 DEBUG: targetUserId:', targetUserId);
      if (targetUserId) {
        console.log('📡 Emitting callAnswer via socket to:', targetUserId);
        // Get current user ID for senderUserId
        const user = JSON.parse(localStorage.getItem('user'));
        const senderUserId = user?.id;
        console.log('🔍 DEBUG: senderUserId:', senderUserId);
        
        socketService.emitCallAnswer({
          callId: call._id,
          answer,
          targetUserId,
          senderUserId  // Include sender's ID so seller knows who to reply to
        });        console.log('✅ Socket emit completed');
      } else {
        console.error('❌ Cannot emit callAnswer: targetUserId is null/undefined');
      }
      console.log("✅ Call answered successfully");

    } catch (error) {
      console.error('❌ Error answering call:', error);
      // Clean up on error
      await endCall();
      throw error;
    }
  }, [initializeLocalStream, createPeerConnection, endCall]);

  // Handle incoming call offer
  const handleIncomingCall = useCallback(async (data) => {
    try {
      console.log('📞 Incoming call received:', data);
      
      // Store call data for later use
      setCurrentCall(data.call);
      currentCallRef.current = data.call;
      setCallType(data.call.callType);

      return data.call;
    } catch (error) {
      console.error('❌ Error handling incoming call:', error);
      throw error;
    }
  }, []);

  // Handle remote answer
  const handleRemoteAnswer = useCallback(async (data) => {
    console.log('🎯🎯🎯 DEBUG handleRemoteAnswer CALLED!');
    console.log('🎯🎯🎯 DEBUG data:', data);
    console.log('📞🚨🚨🚨 REMOTE ANSWER HANDLER TRIGGERED! 🚨🚨🚨');
    
    if (!data) {
      console.error('❌❌❌ ERROR: data is null or undefined!');
      return;
    }
    
    if (!data.answer) {
      console.error('❌❌❌ ERROR: No answer in data!');
      console.error('❌❌❌ Data structure:', data);
      return;
    }
    
    console.log('📞✅ Answer received:', data.answer.type);
    
    if (peerConnection.current) {
      try {
        // Check if remote description is already set to avoid InvalidStateError
        const currentRemoteDescription = peerConnection.current.remoteDescription;
        if (currentRemoteDescription && currentRemoteDescription.type) {
          console.log('⚠️ Remote description already set to:', currentRemoteDescription.type, '- Skipping duplicate');
          return;
        }
        
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(data.answer));
        console.log('✅✅✅ Remote description set from answer');
        
        // Update call status
        setIsCallInProgress(true);
        setIsCallActive(true);
        
        // Notify other user that call is connected
        if (currentCallRef.current) {
          const otherUserId = getOtherUserId();
          if (otherUserId) {
            socketService.emitCallConnected({
              callId: currentCallRef.current._id,
              targetUserId: otherUserId
            });
          }
        }
      } catch (error) {
        console.error('❌ Error setting remote description:', error);
        // Don't end the call on error, just log it
      }
    } else {
      console.error('❌ No peer connection to set answer on');
    }
  }, [getOtherUserId]);
        

  // Handle call connected notification
  const handleCallConnected = useCallback(async (data) => {
    try {
      console.log("📞📞📞📞📞 CALL CONNECTED NOTIFICATION RECEIVED! 📞📞📞📞📞");
      console.log("📞📞📞 DETAILED - Call connected data:", JSON.stringify(data, null, 2));
      console.log("📞 Call connected notification received:", data);
      setIsCallActive(true);
      setIsCallInProgress(false);
      console.log("✅ Call is now active");
    } catch (error) {
      console.error("❌ Error handling call connected:", error);
    }
  }, []);

  // Handle ICE candidate from remote
  const handleRemoteICECandidate = useCallback(async (data) => {
    try {
      console.log('❄️🚨 HANDLE REMOTE ICE CANDIDATE TRIGGERED!');
      console.log('❄️📦 Full ICE candidate data:', JSON.stringify(data));
      console.log('❄️🎯 Data contains candidate?', !!data.candidate);
      console.log('❄️🆔 Call ID in data:', data.callId);
      console.log('❄️👤 Target user ID:', data.targetUserId);
      console.log('❄️🔗 Peer connection exists?', !!peerConnection.current);
      console.log('❄️📞 Current call ref:', currentCallRef.current);
      
      if (peerConnection.current && data.candidate) {
        console.log('❄️➕ Adding ICE candidate to peer connection...');
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(data.candidate));
        console.log('✅ Remote ICE candidate added successfully');
      } else {
        console.log('❌ Cannot add ICE candidate - missing peer connection or candidate');
        if (!peerConnection.current) console.log('❌ Peer connection is null');
        if (!data.candidate) console.log('❌ Candidate data is missing:', data);
      }
    } catch (error) {
      console.error('❌ Error adding remote ICE candidate:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
    }
  }, []);;

  // Handle call ended by remote
  const handleCallEnded = useCallback(async (data) => {
    console.log('📞 Call ended by remote:', data);
    await endCall();
  }, [endCall]);

  // Handle call rejected by remote
  const handleCallRejected = useCallback(async (data) => {
    console.log('📞 Call rejected by remote:', data);
    await endCall();
  }, [endCall]);

  // Reject call
  const rejectCall = useCallback(async (callId) => {
    try {
      console.log('📞 Rejecting call:', callId);
      await callAPI.rejectCall({ callId });
      
      // Emit socket event to notify caller
      if (currentCallRef.current) {
        const targetUserId = getOtherUserId();
        if (targetUserId) {
          socketService.emitCallReject({
            callId: currentCallRef.current._id,
            targetUserId
          });
        }
      }
      
      await endCall();
      console.log('✅ Call rejected');
    } catch (error) {
      console.error('❌ Error rejecting call:', error);
      await endCall();
      throw error;
    }
  }, [endCall, getOtherUserId]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (!localStreamRef.current) return false;

    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      console.log('🎥 Video', videoTrack.enabled ? 'enabled' : 'disabled');
      return videoTrack.enabled;
    }
    return false;
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (!localStreamRef.current) return false;

    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      console.log('🎤 Audio', audioTrack.enabled ? 'enabled' : 'disabled');
      return audioTrack.enabled;
    }
    return false;
  }, []);

  // Setup socket listeners
  useEffect(() => {
    console.log('🔌 Setting up WebRTC socket listeners...');

    // Remove any existing listeners first
    console.log('🔌 Removing old socket listeners...');
    socketService.offIncomingCall?.();
    socketService.offCallAnswered?.();
    socketService.offCallConnected?.();
    socketService.offICECandidate?.();
    socketService.offCallEnded?.();
    socketService.offCallRejected?.();

    // Add new listeners
    console.log('🔌 Adding new socket listeners...');
    console.log('🎯 Listening for iceCandidate events');
    socketService.onIncomingCall(handleIncomingCall);
    socketService.onCallAnswered(handleRemoteAnswer);
    console.log("🎯🎯🎯 DEBUG: Setting up onCallConnected listener with handler:", handleCallConnected);
    socketService.onCallConnected(handleCallConnected);
    console.log("🎯🎯🎯 DEBUG: onCallConnected listener setup complete");
    socketService.onICECandidate(handleRemoteICECandidate);
    socketService.onCallEnded(handleCallEnded);
    socketService.onCallRejected(handleCallRejected);
    console.log('✅ All socket listeners added');
    return () => {
      console.log('🔌 Cleaning up WebRTC socket listeners...');
      socketService.offIncomingCall?.();
      socketService.offCallAnswered?.();
      socketService.offCallConnected?.();
      socketService.offICECandidate?.();
      socketService.offCallEnded?.();
      socketService.offCallRejected?.();
    };
  }, [handleIncomingCall, handleRemoteAnswer, handleRemoteICECandidate, handleCallEnded, handleCallRejected, handleCallConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up WebRTC hook...');
      if (isCallActive || isCallInProgress) {
        endCall();
      }
    };
  }, []); // Empty dependency array - only run on unmount

  

  // Set up call answered listener
  const onCallAnswered = (callback) => {



    console.log('📞 Setting up call answered listener');
    if (socketRef?.current) {
      socketRef.current.on('callAnswered', callback);
        console.log("⚡ Auto-activating call on answer");
        setIsCallActive(true);

        console.log("⚡ Auto-activating call on answer");
        setIsCallActive(true);

    }
  };

  // Set up call connected listener
  const onCallConnected = (callback) => {
    console.log('📞 Setting up call connected listener');
    if (socketRef?.current) {
      socketRef.current.on('callConnected', callback);
    }
  };

  // Set up call ended listener
  const onCallEnded = (callback) => {
    console.log('📞 Setting up call ended listener');
    if (socketRef?.current) {
      socketRef.current.on('callEnded', callback);
    }
  };



  // Set up ICE candidate listener
  const onICECandidate = (callback) => {
    console.log('❄️ Setting up ICE candidate listener');
    if (socketRef?.current) {
      socketRef.current.on('iceCandidate', callback);
    }
  };

  // Clean up incoming call listener
  const offIncomingCall = () => {
    console.log('🧹 Cleaning up incoming call listener');
    if (socketRef?.current) {
      socketRef.current.off('incomingCall');
      console.log('✅ Removed incomingCall listener');
    }
  };

  // Set up call rejected listener
  const onCallRejected = (callback) => {
    console.log('📞 Setting up call rejected listener');
    if (socketRef?.current) {
      socketRef.current.on('callRejected', callback);
    }
  };
  
  // Clean up callanswered listener
  const offCallAnswered = () => {
    console.log('🧹 Cleaning up offCallAnswered listener');
    if (socketRef?.current) {
      socketRef.current.off('callanswered');
      console.log('✅ Removed offCallAnswered listener');
    }
  };
  
  // Clean up callconnected listener
  const offCallConnected = () => {
    console.log('🧹 Cleaning up offCallConnected listener');
    if (socketRef?.current) {
      socketRef.current.off('callconnected');
      console.log('✅ Removed offCallConnected listener');
    }
  };
  
  // Clean up callended listener
  const offCallEnded = () => {
    console.log('🧹 Cleaning up offCallEnded listener');
    if (socketRef?.current) {
      socketRef.current.off('callended');
      console.log('✅ Removed offCallEnded listener');
    }
  };
  // Clean up icecandidate listener
  const offICECandidate = () => {
    console.log('🧹 Cleaning up icecandidate listener');
    if (socketRef?.current) {
      socketRef.current.off('icecandidate');
    }
  };
  // Clean up callrejected listener
  const offCallRejected = () => {
    console.log('🧹 Cleaning up callrejected listener');
    if (socketRef?.current) {
      socketRef.current.off('callrejected');
    }
  };
  // Clean up callaccepted listener
  const offCallAccepted = () => {
    console.log('🧹 Cleaning up callaccepted listener');
    if (socketRef?.current) {
      socketRef.current.off('callaccepted');
    }
  };
  // Clean up calldeclined listener
  const offCallDeclined = () => {
    console.log('🧹 Cleaning up calldeclined listener');
    if (socketRef?.current) {
      socketRef.current.off('calldeclined');
    }
  };
  // Clean up callmissed listener
  const offCallMissed = () => {
    console.log('🧹 Cleaning up callmissed listener');
    if (socketRef?.current) {
      socketRef.current.off('callmissed');
    }
  };
  // Clean up peerconnection listener
  const offPeerConnection = () => {
    console.log('🧹 Cleaning up peerconnection listener');
    if (socketRef?.current) {
      socketRef.current.off('peerconnection');
    }
  };
  // Clean up remotestream listener
  const offRemoteStream = () => {
    console.log('🧹 Cleaning up remotestream listener');
    if (socketRef?.current) {
      socketRef.current.off('remotestream');
    }
  };
  // Clean up localstream listener
  const offLocalStream = () => {
    console.log('🧹 Cleaning up localstream listener');
    if (socketRef?.current) {
      socketRef.current.off('localstream');
    }
  };
  // Clean up callerror listener
  const offCallError = () => {
    console.log('🧹 Cleaning up callerror listener');
    if (socketRef?.current) {
      socketRef.current.off('callerror');
    }
  };
  // Set up incoming call listener
  const onIncomingCall = (callback) => {
    console.log('📞 Setting up incoming call listener');
    if (socketRef?.current) {
      socketRef.current.on('incomingCall', callback);
    }
  };
  // Make debug functions available globally
  if (typeof window !== "undefined") {
    console.log("🎮 Debug commands available: window.debugWebRTC.playRemoteAudio() and window.debugWebRTC.checkStatus()");
  }

  return {
    // State
    isCallActive,
    isCallInProgress,
    callType,
    localStream,
    remoteStream,
    currentCall,

    // Methods
    startCall,
    answerCall,
    endCall,
    rejectCall,
    toggleVideo,
    toggleAudio,
    handleIncomingCall,
    offIncomingCall,
    offCallAnswered,
    offCallConnected,
    offCallEnded,
    offICECandidate,
    offCallRejected,
    offCallAccepted,
    offCallDeclined,
    offCallMissed,
    offPeerConnection,
    offRemoteStream,
    offLocalStream,
    offCallError,
    onIncomingCall,
    onCallAnswered,
    onCallConnected,
    onCallEnded,
    onICECandidate,
    onCallRejected,
    callStatus,
    error};
};

export default useWebRTC;










// Add this at the end of the file to ensure audio plays
export const ensureAudioPlayback = () => {
  if (remoteStreamRef.current) {
    const audioTracks = remoteStreamRef.current.getAudioTracks();
    if (audioTracks.length > 0) {
      console.log("🔊 Remote audio tracks found:", audioTracks.length);
      const audioElement = new Audio();
      audioElement.srcObject = remoteStreamRef.current;
      audioElement.play().catch(e => console.error("❌ Audio playback failed:", e));
    }
  }
};

// Make debug functions available globally
if (typeof window !== 'undefined') {
  console.log("🎮 Debug commands available: window.debugWebRTC.playRemoteAudio() and window.debugWebRTC.checkStatus()");
}
